import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Snackbar,
  Alert
} from "@mui/material";

export default function AgentPatchManager() {
  const [filePath, setFilePath] = useState("backend/gui_viewer_api/routes/example.py");
  const [instruction, setInstruction] = useState("");
  const [changeId, setChangeId] = useState("");
  const [previewDiff, setPreviewDiff] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const handlePreview = async () => {
    setLoading(true);
    setConfirming(false);
    setPreviewDiff(null);
    try {
      const res = await fetch("/api/agent-patch-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_path: filePath, instruction, change_id: changeId }),
      });
      const data = await res.json();
      if (res.ok) {
        setPreviewDiff(data.diff || "No changes.");
        setConfirming(true);
      } else {
        setSnackbar({ open: true, message: data.error || "Preview failed", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Preview request failed", severity: "error" });
    }
    setLoading(false);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/agent-patch-confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_path: filePath, instruction, change_id: changeId }),
      });
      const data = await res.json();
      if (res.ok) {
        setSnackbar({ open: true, message: "Patch successfully applied.", severity: "success" });
        setPreviewDiff(null);
        setConfirming(false);
      } else {
        setSnackbar({ open: true, message: data.error || "Confirm failed", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Confirm request failed", severity: "error" });
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setPreviewDiff(null);
    setConfirming(false);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        🧠 Agent Patch Manager
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
        <TextField
          label="Backend File Path"
          value={filePath}
          onChange={(e) => setFilePath(e.target.value)}
          fullWidth
        />
        <TextField
          label="Instruction"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          fullWidth
          multiline
          rows={4}
        />
        <TextField
          label="Change ID (Optional)"
          value={changeId}
          onChange={(e) => setChangeId(e.target.value)}
          fullWidth
        />
        <Button
          variant="contained"
          onClick={handlePreview}
          disabled={loading}
        >
          {loading ? "Generating Preview..." : "Submit for Preview"}
        </Button>
      </Box>

      {previewDiff && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>🔍 Patch Preview:</Typography>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto', backgroundColor: "#f5f5f5" }}>
            <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}>
              {previewDiff}
            </pre>
          </Paper>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="contained" color="success" onClick={handleConfirm} disabled={loading}>
              {loading ? "Applying..." : "✅ Confirm Patch"}
            </Button>
            <Button variant="outlined" color="error" onClick={handleCancel}>
              ❌ Cancel
            </Button>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
